/**
 * IBSGroupsView (P428.2). Route: /twin/ibs-groups.
 *
 * The groups a board governs, read the way the portfolio reads services:
 * each group's colour is its WORST member's -- "Everyday payments:
 * green" must never hide a red service inside it -- beside its derived
 * tolerance (the tightest member's, named), how many members are in
 * live breach, and its accountable owner. Admitted services outside
 * every group are listed below, because that is the gap the check
 * flags. Membership is edited on the register, where the rationale is
 * asked for.
 */
import { FirstMeetings } from "shared/glossary/FirstMeetings";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";

import { api } from "shared/api/client";
import { canRunOperations } from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { GroupRegisterDownload } from "shared/groups/GroupRegisterDownload";

interface MemberState {
  ibs_service_id: string;
  code: string;
  name: string;
  admission_status: string;
  mvb_flag: boolean;
  resilience: string | null;
  impact_tolerance_hours: number | null;
  live_verdict: string | null;
  /** Round 16 O4 -- an approved declaration stands behind the hours. */
  tolerance_signed?: boolean;
}

interface GroupState {
  code: string;
  name: string;
  kind: string;
  is_reserved: boolean;
  owner_name: string | null;
  /** Round 19 O10 -- a person, not a role, is recorded as accountable. */
  owner_is_person?: boolean;
  resilience: string | null;
  breached_now: number;
  mvb_members: number;
  derived_tolerance_hours: number | null;
  derived_tolerance_from: string | null;
  /** Round 16 O4 -- null where the group derives no tolerance. */
  derived_tolerance_signed?: boolean | null;
  members: MemberState[];
}

interface GroupsState {
  groups: GroupState[];
  ungrouped_admitted: MemberState[];
}

const RES_TONE: Record<string, string> = {
  red: "border-rag-red",
  amber: "border-rag-amber",
  green: "border-rag-green",
};

const DOT: Record<string, string> = {
  red: "bg-rag-red",
  amber: "bg-rag-amber",
  green: "bg-rag-green",
};

function Member({ m }: { m: MemberState }) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center gap-2 text-sm">
      <span
        aria-hidden="true"
        className={`inline-block h-2 w-2 rounded-full ${m.resilience ? DOT[m.resilience] : "bg-slate-300"}`}
      />
      <Link
        to={`/twin/ibs/${m.ibs_service_id}`}
        className="text-slate-900 hover:text-brand-brassDark hover:underline"
      >
        {m.name}
      </Link>
      {/* Round 16 O4 -- the page said every admitted service sits in a
          group, over members two of three of which were candidates. A
          member not yet admitted says so; an admitted one reads as
          before. */}
      {m.admission_status !== "admitted" && (
        <span
          data-testid={`ibs-group-member-candidate-${m.ibs_service_id}`}
          className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-900"
        >
          {t("modules.twin.ibsGroups.notAdmitted", {
            status: t(`modules.twin.ibsGroups.status.${m.admission_status}`, {
              defaultValue: m.admission_status,
            }),
          })}
        </span>
      )}
      {m.mvb_flag && (
        <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-900">
          {t("screens.iBSPortfolioView.mvb")}
        </span>
      )}
      {m.live_verdict === "breached" && (
        <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] text-rose-800">
          {t("modules.twin.ibsGroups.breached")}
        </span>
      )}
    </li>
  );
}

export function IBSGroupsView() {
  const { t } = useTranslation();
  const me = useMe();
  const q = useQuery({
    queryKey: ["twin", "ibs-groups"],
    queryFn: () => api.get<GroupsState>("/v1/twin/ibs-groups"),
    refetchInterval: 60_000,
  });
  const data = q.data;
  const groups = Array.isArray(data?.groups) ? data.groups : [];
  /* Round 16 O5 -- a link from an IBS page lands on its group. */
  const { hash } = useLocation();
  const targeted = hash ? decodeURIComponent(hash.slice(1)) : null;
  const loaded = groups.length > 0;
  useEffect(() => {
    if (!loaded || !hash) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView?.({
      block: "start",
    });
  }, [loaded, hash]);
  const ungrouped = Array.isArray(data?.ungrouped_admitted)
    ? data.ungrouped_admitted
    : [];

  return (
    <article data-testid="ibs-groups-view" className="max-w-5xl space-y-5">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon name="ibs" className="me-2 inline-block h-6 w-6 align-[-4px]" />
          {t("modules.twin.ibsGroups.heading")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">
          {t("modules.twin.ibsGroups.body")}
        </p>
        {/* Round 17 O8 -- the terms the page uses, taught once. */}
        <FirstMeetings terms={["ibs_group", "mvb"]} />
        {canRunOperations(me.data) && <GroupRegisterDownload />}
        {/* Round 17 Q1 -- the editor was reached only through a
            breadcrumb; this page showed the groups and offered no way to
            make one. */}
        {canRunOperations(me.data) && (
          <Link
            to="/foundation/admin/ibs-groups"
            data-testid="ibs-groups-edit"
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1 text-xs font-medium text-brand-brassDark hover:bg-brand-brass/10"
          >
            {t("modules.twin.ibsGroups.newOrEdit")}
          </Link>
        )}
      </header>

      {q.isError && (
        <p className="text-sm text-rose-700">{t("modules.twin.ibsGroups.error")}</p>
      )}

      <ul className="grid gap-3 md:grid-cols-2">
        {groups.map((g) => (
          <li
            key={g.code}
            id={g.code}
            data-testid={`ibs-group-${g.code}`}
            data-resilience={g.resilience ?? "none"}
            // Round 17 O8 -- the group a link arrived for is marked.
            data-targeted={targeted === g.code ? "true" : undefined}
            className={`space-y-2 rounded-lg border-s-4 bg-white p-3 shadow-card ${g.resilience ? RES_TONE[g.resilience] : "border-slate-200"} ${targeted === g.code ? "ring-2 ring-brand-brass" : ""}`}
          >
            {/* Round 18 O9 -- the ring alone did not read as a mark: the
                group a link arrived for says so in words. */}
            {targeted === g.code && (
              <p
                data-testid={`ibs-group-arrived-${g.code}`}
                className="text-[11px] font-medium text-brand-brassDark"
              >
                {t("modules.twin.ibsGroups.arrivedFor")}
              </p>
            )}
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="font-display text-lg text-slate-900">{g.name}</h2>
              {g.is_reserved && (
                <span
                  title={t("modules.twin.ibsGroups.reservedWhy")}
                  className="rounded bg-paper-2 px-1.5 py-0.5 text-[10px] text-slate-600"
                >
                  {t("modules.twin.ibsGroups.reserved")}
                </span>
              )}
            </div>
            <p
              className="text-xs text-slate-600"
              data-testid={`ibs-group-owner-${g.code}`}
            >
              {!g.owner_name
                ? t("modules.twin.ibsGroups.noOwner")
                : g.owner_is_person === false
                  ? t("modules.twin.ibsGroups.ownerNotAPerson", { owner: g.owner_name })
                  : t("modules.twin.ibsGroups.owner", { owner: g.owner_name })}
            </p>
            <p
              className="text-xs text-slate-600"
              data-testid={`ibs-group-tolerance-${g.code}`}
            >
              {g.derived_tolerance_hours !== null
                ? t(
                    /* Round 16 O4 -- the hours rested on a column with no
                       signed declaration behind it; say which it is. */
                    g.derived_tolerance_signed === false
                      ? "modules.twin.ibsGroups.toleranceUnsigned"
                      : "modules.twin.ibsGroups.tolerance",
                    {
                      hours: g.derived_tolerance_hours,
                      from: g.derived_tolerance_from,
                    },
                  )
                : t("modules.twin.ibsGroups.noTolerance")}
              {g.breached_now > 0 && (
                <>
                  {" · "}
                  <span
                    className="text-rose-800"
                    title={t("modules.twin.ibsGroups.breachedWhy")}
                  >
                    {t("modules.twin.ibsGroups.breachedCount", {
                      count: g.breached_now,
                    })}
                  </span>
                </>
              )}
            </p>
            {g.members.length === 0 ? (
              <p className="text-xs italic text-slate-500">
                {t("modules.twin.ibsGroups.empty")}
              </p>
            ) : (
              <ul className="space-y-1">
                {g.members.map((m) => (
                  <Member key={m.ibs_service_id} m={m} />
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>

      {data && (
        <section data-testid="ibs-groups-ungrouped" className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-900">
            {t("modules.twin.ibsGroups.ungroupedHeading", { count: ungrouped.length })}
          </h2>
          {ungrouped.length === 0 ? (
            <p className="text-xs text-slate-500">
              {t("modules.twin.ibsGroups.allGrouped")}
            </p>
          ) : (
            <>
              <ul className="space-y-1">
                {ungrouped.map((m) => (
                  <Member key={m.ibs_service_id} m={m} />
                ))}
              </ul>
              <Link
                to="/foundation/admin/ibs-groups"
                className="text-xs text-brand-brassDark hover:underline"
              >
                {t("modules.twin.ibsGroups.placeThem")}
              </Link>
            </>
          )}
        </section>
      )}
    </article>
  );
}
